// Panneau talkie-walkie du répartiteur: parler à un chauffeur ou à tous, écouter les réponses.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PTTButton from "@/components/PTTButton";
import usePTTInbox from "@/hooks/usePTTInbox";
import { Radio, Users, Play, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const clock = (iso) => (iso || "").slice(11, 16);

export default function DispatchPTT() {
  const [drivers, setDrivers] = useState([]);
  const [target, setTarget] = useState("all");
  const [history, setHistory] = useState([]);
  const { playing, blocked, unlock, replay } = usePTTInbox({ pollMs: 5000 });

  const loadHistory = () => api.get("/ptt/history?limit=25")
    .then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    loadHistory();
    const t = setInterval(loadHistory, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="rounded-lg border border-border bg-card" data-testid="dispatch-ptt">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-slate-400" />
          <span className="font-display font-semibold">Talkie-walkie</span>
        </div>
        {playing && (
          <Badge className="bg-emerald-600" data-testid="ptt-now-playing">
            <Volume2 className="w-3 h-3 mr-1" /> {playing.from_name}
          </Badge>
        )}
      </div>

      <div className="p-5 space-y-3">
        {blocked && (
          <button onClick={unlock} data-testid="ptt-unlock-audio"
            className="w-full text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
            Cliquez ici une fois pour autoriser la lecture automatique des messages vocaux.
          </button>
        )}

        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setTarget("all")} data-testid="ptt-target-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
              target === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border text-slate-600"}`}>
            <Users className="w-3 h-3 inline mr-1" /> Tous les chauffeurs
          </button>
          {drivers.map((d) => (
            <button key={d.id} onClick={() => setTarget(d.id)} data-testid={`ptt-target-${d.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                target === d.id ? "bg-primary text-primary-foreground border-primary" : "border-border text-slate-600"}`}>
              {d.first_name} {d.last_name}
            </button>
          ))}
        </div>

        <PTTButton
          channel={target === "all" ? "all" : "driver"}
          driverId={target === "all" ? null : target}
          label={target === "all" ? "MAINTENIR POUR PARLER À TOUS" : "MAINTENIR POUR PARLER"}
          onSent={loadHistory}
        />
        <p className="text-[11px] text-muted-foreground">
          Maintenez le bouton, parlez, relâchez : le message est joué automatiquement dans l'autobus (60 s max).
        </p>

        <div className="divide-y divide-border border-t border-border max-h-64 overflow-y-auto -mx-5 px-5">
          {history.map((c) => (
            <div key={c.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`ptt-clip-${c.id}`}>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0"
                onClick={() => replay(c)} data-testid={`ptt-replay-${c.id}`}>
                <Play className="w-4 h-4" />
              </Button>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {c.from_role === "driver" ? c.from_name : `→ ${c.channel === "all" ? "tous les chauffeurs" : (drivers.find((d) => d.id === c.driver_id) ? `${drivers.find((d) => d.id === c.driver_id).first_name} ${drivers.find((d) => d.id === c.driver_id).last_name}` : "chauffeur")}`}
                </div>
                <div className="text-xs text-muted-foreground">
                  {clock(c.created_at)} · {Math.round(c.duration_sec || 0)} s
                  {c.from_role !== "driver" && (c.played_at ? " · écouté" : " · non écouté")}
                </div>
              </div>
              {c.from_role === "driver" && <Badge variant="outline" className="text-[10px]">chauffeur</Badge>}
            </div>
          ))}
          {!history.length && <div className="py-6 text-sm text-muted-foreground">Aucun message vocal.</div>}
        </div>
      </div>
    </div>
  );
}
